/**
 * serviceMeta — vibe-designer 2026-06-29 Knowledge redesign.
 *
 * Single source of truth for each knowledge system's visual identity: one hue,
 * one icon, one help tooltip — reused on the sidebar dot, the stat card, and the
 * ingest target so a service looks the same everywhere.
 *
 * Color class names are written as FULL LITERAL strings (not template-built) so
 * Tailwind's JIT scanner emits them — runtime-computed `bg-vibe-svc-${k}` would
 * be purged from the production bundle (same hazard the SightImageGrid safelist
 * guards against; see tailwind.config.js).
 */
import {
  Brain, Network, Database, Eye, Workflow, BookOpen, Globe, Zap,
  type LucideIcon,
} from 'lucide-react';

export type KnowledgeServiceKey =
  | 'mind' | 'cortex' | 'brain' | 'sight' | 'cognition' | 'obsidian' | 'web_search'
  | 'synapse';

export interface ServiceMeta {
  /** Short display label used on cards/targets. */
  label: string;
  /** `bg-vibe-svc-*` — the service dot / accent band fill. */
  dotClass: string;
  /** `text-vibe-svc-*` — icon / text accent. */
  textClass: string;
  /** `border-vibe-svc-*` — card edge accent. */
  borderClass: string;
  /** Lucide icon for the service. */
  Icon: LucideIcon;
  /** One-line help tooltip (plain text, drop into a `title=`). */
  tooltip: string;
}

export const SERVICE_META: Record<KnowledgeServiceKey, ServiceMeta> = {
  mind: {
    label: 'THE MIND',
    dotClass: 'bg-vibe-svc-mind',
    textClass: 'text-vibe-svc-mind',
    borderClass: 'border-vibe-svc-mind',
    Icon: Brain,
    tooltip: 'THE MIND — pgvector RAG store. The primary searchable corpus; honors pillar tags.',
  },
  cortex: {
    label: 'THE CORTEX',
    dotClass: 'bg-vibe-svc-cortex',
    textClass: 'text-vibe-svc-cortex',
    borderClass: 'border-vibe-svc-cortex',
    Icon: Network,
    tooltip: 'THE CORTEX — Graphiti knowledge graph. Extracts entities + relationships (materializes ~1–2 min after ingest).',
  },
  brain: {
    label: 'THE BRAIN',
    dotClass: 'bg-vibe-svc-brain',
    textClass: 'text-vibe-svc-brain',
    borderClass: 'border-vibe-svc-brain',
    Icon: Database,
    tooltip: 'THE BRAIN — ChromaDB legacy store. Currently offline on this tenant; ingest is disabled.',
  },
  sight: {
    label: 'THE SIGHT',
    dotClass: 'bg-vibe-svc-sight',
    textClass: 'text-vibe-svc-sight',
    borderClass: 'border-vibe-svc-sight',
    Icon: Eye,
    tooltip: 'THE SIGHT — multimodal image search. Images route here automatically; text targets are skipped.',
  },
  cognition: {
    label: 'THE COGNITION',
    dotClass: 'bg-vibe-svc-cognition',
    textClass: 'text-vibe-svc-cognition',
    borderClass: 'border-vibe-svc-cognition',
    Icon: Workflow,
    tooltip: 'THE COGNITION — cognee ECL pipeline (LLM extraction → graph → memify).',
  },
  obsidian: {
    label: 'OBSIDIAN',
    dotClass: 'bg-vibe-svc-obsidian',
    textClass: 'text-vibe-svc-obsidian',
    borderClass: 'border-vibe-svc-obsidian',
    Icon: BookOpen,
    tooltip: 'OBSIDIAN — SilverBullet wiki. Ingested notes become editable wiki pages.',
  },
  web_search: {
    label: 'Web Search',
    dotClass: 'bg-vibe-svc-websearch',
    textClass: 'text-vibe-svc-websearch',
    borderClass: 'border-vibe-svc-websearch',
    Icon: Globe,
    tooltip: 'Web Search — SearXNG meta-search. Query-time only; nothing is stored here.',
  },
  synapse: {
    label: 'THE SYNAPSE',
    dotClass: 'bg-vibe-svc-synapse',
    textClass: 'text-vibe-svc-synapse',
    borderClass: 'border-vibe-svc-synapse',
    Icon: Zap,
    tooltip:
      'THE SYNAPSE — the knowledge loop. Compiles MIND/CORTEX knowledge into tenant wiki articles (3 AM cron or manual compile).',
  },
};
